import type { ScanMatch } from '@siliconx/shared';
import { api } from './api';

/**
 * Client wiring + pure helpers for the scan-intake capture flow (RUF-500).
 *
 * API surface (apps/api/src/routes/client/scans.ts):
 *   POST /api/client/scans            {decodedValue, captureMethod} → {scanId, match}
 *   POST /api/client/scans/upload-url {scanId?}                     → {url, key}
 *   POST /api/client/scans/:id/ocr    {imageKey?}                   → {scanId, match, needsManualEntry?}
 *   POST /api/client/scans/:id/select {skuId} | {none:true}
 *
 * Quota denials are 429 with a machine-readable error code and Retry-After;
 * feature_disabled is 503. Those must disable the scanning UI (per-code copy)
 * without touching manual form entry — see describeScanBlock().
 */

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export class ScanRequestError extends Error {
  status: number;
  code: string | null;
  retryAfter: string | null;

  constructor(status: number, code: string | null, message: string, retryAfter: string | null) {
    super(message);
    this.name = 'ScanRequestError';
    this.status = status;
    this.code = code;
    this.retryAfter = retryAfter;
  }
}

export interface ScanBlock {
  code: string;
  /** 'photo' — only the photo/OCR path is out; 'all' — every scan path is out. */
  scope: 'photo' | 'all';
  message: string;
}

/**
 * Map a 429/503 error code to user-facing copy + which capture paths it
 * disables. Returns null for codes that are not scan-blocking (e.g. plain
 * validation errors) — those surface inline instead.
 */
export function describeScanBlock(code: string | null | undefined): ScanBlock | null {
  switch (code) {
    case 'org_quota':
      return {
        code,
        scope: 'all',
        message:
          "Your organization reached today's scan limit. Scanning reopens tomorrow — you can keep adding assets manually.",
      };
    case 'user_quota':
      return {
        code,
        scope: 'all',
        message:
          "You reached your personal scan limit for today. Scanning reopens tomorrow — manual entry still works.",
      };
    case 'ocr_quota':
      return {
        code,
        scope: 'photo',
        message:
          'Photo recognition reached its daily limit. Live barcode scanning and manual entry still work.',
      };
    case 'suspended':
      return {
        code,
        scope: 'all',
        message:
          'Scanning is paused for your account until tomorrow after unusual activity. Manual entry still works.',
      };
    case 'sequential_pattern':
      return {
        code,
        scope: 'all',
        message:
          'Scanning was paused after an unusual pattern of identifiers. Manual entry still works.',
      };
    case 'feature_disabled':
      return {
        code,
        scope: 'all',
        message: 'Scanning is temporarily unavailable. You can add assets manually in the meantime.',
      };
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Pure helpers (unit-tested in __tests__/scan.test.ts)
// ---------------------------------------------------------------------------

/** Case/whitespace-insensitive model comparison key. */
export function normalizeModelName(model: string | null | undefined): string {
  return (model ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Does a new scan disagree with the model already locked on the form?
 * A scan with no model can't contradict anything; an empty lock accepts all.
 */
export function isModelMismatch(
  lockedModel: string | null | undefined,
  scannedModel: string | null | undefined,
): boolean {
  const locked = normalizeModelName(lockedModel);
  const scanned = normalizeModelName(scannedModel);
  if (!locked || !scanned) return false;
  return locked !== scanned;
}

/**
 * Fill a scanned serial into the per-unit serial list: first empty slot wins;
 * when every slot is taken the list grows by one (quantity += 1). Returns the
 * new list unchanged when the serial is already present (case-insensitive) or
 * blank — re-scanning the same unit must not duplicate rows.
 */
export function fillSerial(serials: string[], serial: string | null | undefined): string[] {
  const v = (serial ?? '').trim();
  if (!v) return serials;
  const key = v.toLowerCase();
  if (serials.some((s) => s.trim().toLowerCase() === key)) return serials;
  const empty = serials.findIndex((s) => !s.trim());
  if (empty >= 0) return serials.map((s, i) => (i === empty ? v : s));
  return [...serials, v];
}

/** Longest edge (px) a label photo is downscaled to before upload. */
export const MAX_PHOTO_EDGE = 2048;

/** Downscale dimensions preserving aspect ratio; no-op below the cap. */
export function scaledDimensions(
  width: number,
  height: number,
  maxEdge: number = MAX_PHOTO_EDGE,
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxEdge) return { width, height };
  const ratio = maxEdge / longest;
  return { width: Math.round(width * ratio), height: Math.round(height * ratio) };
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

/** Throws ScanRequestError (preserving code/retryAfter for describeScanBlock) for a non-ok hc response body. */
async function scanError(res: { status: number; json(): Promise<unknown>; headers: Headers }): Promise<never> {
  const body = (await res.json().catch(() => ({}))) as {
    error?: { code?: string; message?: string } | string;
  };
  const err = typeof body.error === 'object' ? body.error : undefined;
  throw new ScanRequestError(
    res.status,
    err?.code ?? null,
    err?.message ?? `HTTP ${res.status}`,
    res.headers.get('Retry-After'),
  );
}

export async function createScan(decodedValue: string, captureMethod: 'barcode' | 'manual') {
  const res = await api.api.client.scans.$post({ json: { decodedValue, captureMethod } });
  if (!res.ok) return scanError(res);
  return res.json();
}

export async function requestUploadUrl(scanId?: string) {
  const res = await api.api.client.scans['upload-url'].$post({
    json: scanId ? { scanId } : {},
  });
  if (!res.ok) return scanError(res);
  return res.json();
}

/** Presigned PUT — plain fetch, no auth headers (the signature IS the auth). */
export async function uploadScanPhoto(url: string, blob: Blob): Promise<void> {
  const res = await fetch(url, {
    method: 'PUT',
    body: blob,
    headers: { 'Content-Type': 'image/jpeg' },
  });
  if (!res.ok) {
    throw new ScanRequestError(res.status, null, `photo upload failed (HTTP ${res.status})`, null);
  }
}

export interface ScanOcrResponse {
  scanId: string;
  match: ScanMatch;
  needsManualEntry?: boolean;
}

export async function ocrScan(scanId: string, imageKey: string): Promise<ScanOcrResponse> {
  const res = await api.api.client.scans[':id'].ocr.$post({
    param: { id: scanId },
    json: { imageKey },
  });
  if (!res.ok) return scanError(res);
  return res.json();
}

export async function selectScanSku(scanId: string, body: { skuId: string } | { none: true }) {
  const res = await api.api.client.scans[':id'].select.$post({
    param: { id: scanId },
    json: body,
  });
  if (!res.ok) return scanError(res);
  return res.json();
}

/**
 * Photo-first OCR flow: the upload-url call mints a fresh scan key, so the
 * scan row is created by /ocr. But /ocr needs a scan id in the path — for the
 * photo-first case the key's uuid stem IS the scan id the server expects to
 * create. The backend contract (scans.ts) keys photos as
 * scans/{org}/{id}.jpg; deriving the id from the returned key keeps a single
 * source of truth.
 */
export function scanIdFromKey(key: string): string | null {
  const m = /\/([0-9a-f-]{36})\.jpg$/i.exec(key);
  return m?.[1] ?? null;
}

// ---------------------------------------------------------------------------
// Photo downscale (DOM-dependent — not unit-tested; math lives in
// scaledDimensions above)
// ---------------------------------------------------------------------------

/** Downscale to ≤ maxEdge on the longest side and re-encode as JPEG. */
export async function downscalePhoto(file: Blob, maxEdge: number = MAX_PHOTO_EDGE): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  try {
    const { width, height } = scaledDimensions(bitmap.width, bitmap.height, maxEdge);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas 2d context unavailable');
    ctx.drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.85),
    );
    if (!blob) throw new Error('photo encode failed');
    return blob;
  } finally {
    bitmap.close();
  }
}
